// services/v3/datasource/normalize.ts — RTDB payload normalisation (SPEC §4.2)
//
// Firebase RTDB hands back either arrays or maps for the same node depending on
// how the POS wrote it (sparse arrays come back as `{ "0": ..., "3": ... }`).
// Everything here is pure and defensive: never throw, always return the shape
// the catalog store expects.
import type {
  ChannelPriceMap, MenuGroup, Product, ProductAllowMap, ProductsPayload,
  SettingRecord, SettingsPayload,
} from '~/types/v3/entities'

/** Truthy plain object (not an array, not null). */
const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v)

/**
 * Turns an RTDB `array | map | null` node into `[key, value]` pairs,
 * dropping null/undefined members. Array indexes become string keys.
 */
function entriesOf(raw: unknown): Array<[string, unknown]> {
  if (Array.isArray(raw)) {
    const out: Array<[string, unknown]> = []
    raw.forEach((v, i) => { if (v !== null && v !== undefined) out.push([String(i), v]) })
    return out
  }
  if (isRecord(raw)) {
    return Object.entries(raw).filter(([, v]) => v !== null && v !== undefined)
  }
  return []
}

/** `parseFloat`-ish coercion that tolerates strings and returns `fallback` for junk. */
export function toNumber(v: unknown, fallback = 0): number {
  if (typeof v === 'number') return Number.isFinite(v) ? v : fallback
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v)
    return Number.isFinite(n) ? n : fallback
  }
  return fallback
}

/** `parseInt` coercion used for option `limit/min/max` (they may be strings). */
function toIntOrUndefined(v: unknown): number | undefined {
  if (v === null || v === undefined || v === '') return undefined
  const n = typeof v === 'number' ? Math.trunc(v) : parseInt(String(v), 10)
  return Number.isFinite(n) ? n : undefined
}

function toRowOrUndefined(v: unknown): number | undefined {
  if (v === null || v === undefined || v === '') return undefined
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) ? n : undefined
}

/** Stable sort by `row` when at least one member has one; keeps source order otherwise. */
function sortByRow<T extends { row?: number }>(list: T[]): T[] {
  const hasRow = list.some((x) => typeof x.row === 'number')
  if (!hasRow) return list
  return list
    .map((value, index) => ({ value, index }))
    .sort((a, b) => {
      const ar = typeof a.value.row === 'number' ? a.value.row : Number.MAX_SAFE_INTEGER
      const br = typeof b.value.row === 'number' ? b.value.row : Number.MAX_SAFE_INTEGER
      return ar === br ? a.index - b.index : ar - br
    })
    .map((x) => x.value)
}

// ---------------------------------------------------------------------------
// settings
// ---------------------------------------------------------------------------

/**
 * `stores/{storeId}/settings` is either an array of `{ settingId, value }` or a
 * map. A map member is normally still `{ settingId, value }`, but we also accept
 * `{ [settingId]: value }` (key becomes the settingId).
 */
export function normalizeSettings(raw: unknown): SettingsPayload {
  const list: SettingRecord[] = []
  const seen = new Set<string>()

  for (const [key, entry] of entriesOf(raw)) {
    let settingId: string
    let value: unknown

    if (isRecord(entry) && typeof entry.settingId === 'string' && entry.settingId !== '') {
      settingId = entry.settingId
      value = 'value' in entry ? entry.value : entry
    } else {
      // map keyed by settingId whose member is the bare value
      if (/^\d+$/.test(key)) continue // positional key with no settingId => unusable
      settingId = key
      value = entry
    }

    if (seen.has(settingId)) {
      // last write wins, mirroring lodash `_.find` on a de-duplicated node
      const at = list.findIndex((x) => x.settingId === settingId)
      if (at >= 0) list[at] = { settingId, value }
      continue
    }
    seen.add(settingId)
    list.push({ settingId, value })
  }

  return { list }
}

/** Raw value of a setting record, or `undefined` when absent. */
export function pickSettingValue(settings: SettingsPayload | null | undefined, id: string): unknown {
  return settings?.list.find((x) => x.settingId === id)?.value
}

/** `channel_price` setting -> `{ channelId: { productId: price } }` with numeric prices. */
export function pickChannelPrice(settings: SettingsPayload | null | undefined): ChannelPriceMap {
  const raw = pickSettingValue(settings, 'channel_price')
  const out: ChannelPriceMap = {}
  for (const [channelId, channel] of entriesOf(raw)) {
    const prices: Record<string, number> = {}
    for (const [productId, price] of entriesOf(channel)) {
      if (typeof price === 'number' || typeof price === 'string') {
        const n = toNumber(price, Number.NaN)
        if (Number.isFinite(n)) prices[productId] = n
      }
    }
    out[channelId] = prices
  }
  return out
}

/** QR menu-set setting -> `{ productId: true }`; empty map when the setting is missing. */
export function pickMenuSet(
  settings: SettingsPayload | null | undefined,
  settingId: string | null | undefined,
): ProductAllowMap {
  if (!settingId) return {}
  const raw = pickSettingValue(settings, settingId)
  const out: ProductAllowMap = {}
  for (const [productId, allowed] of entriesOf(raw)) {
    if (allowed) out[productId] = true
  }
  return out
}

// ---------------------------------------------------------------------------
// products
// ---------------------------------------------------------------------------

function normalizeProduct(raw: unknown, key: string): Product | null {
  if (!isRecord(raw)) return null
  const productId = typeof raw.productId === 'string' && raw.productId !== ''
    ? raw.productId
    : (typeof raw.productId === 'number' ? String(raw.productId) : key)
  if (!productId) return null

  const p: Product = {
    ...raw,
    productId,
    name: typeof raw.name === 'string' ? raw.name : String(raw.name ?? ''),
    price: toNumber(raw.price, 0),
  }

  const row = toRowOrUndefined(raw.row)
  if (row === undefined) delete p.row; else p.row = row

  for (const field of ['limit', 'min', 'max', 'level'] as const) {
    const n = toIntOrUndefined(raw[field])
    if (n === undefined) delete p[field]; else p[field] = n
  }

  if (raw.items !== undefined && raw.items !== null) {
    p.items = normalizeProductList(raw.items)
  }

  return p
}

function normalizeProductList(raw: unknown): Product[] {
  const list: Product[] = []
  for (const [key, entry] of entriesOf(raw)) {
    const p = normalizeProduct(entry, key)
    if (p) list.push(p)
  }
  return sortByRow(list)
}

function normalizeGroups(raw: unknown): MenuGroup[] {
  const groups: MenuGroup[] = []
  for (const [key, entry] of entriesOf(raw)) {
    if (!isRecord(entry)) continue
    const productId = typeof entry.productId === 'string' && entry.productId !== ''
      ? entry.productId
      : (typeof entry.productId === 'number' ? String(entry.productId) : key)
    if (!productId) continue

    const group: MenuGroup = {
      ...(entry as Record<string, unknown>),
      productId,
      name: typeof entry.name === 'string' ? entry.name : String(entry.name ?? ''),
      // legacy guards against groups with no `items` (useQrcodeMenuSetting); keep [] here
      items: normalizeProductList(entry.items),
    } as MenuGroup

    const row = toRowOrUndefined(entry.row)
    if (row === undefined) delete group.row; else group.row = row

    groups.push(group)
  }
  return sortByRow(groups)
}

/** `stores/{storeId}/products` -> `{ list, computed }`, arrays sorted by `row`. */
export function normalizeProducts(raw: unknown): ProductsPayload {
  const src = isRecord(raw) ? raw : {}
  return {
    list: normalizeProductList(src.list),
    computed: normalizeGroups(src.computed),
  }
}
